"use client";

import Image from "next/image";
import { ArrowUpRight } from "lucide-react";
import { Link } from "@/i18n/routing";
import { columnistImages, columnistBios } from "@/fixtures/ejinsight/fixtures";
// The route keys on a SLUG. Built by hand as `encodeURIComponent(name)` here,
// every columnist link 404'd.
import {
  ejInsightColumnistRoute,
  EJINSIGHT_ARTICLE_ROUTE,
} from "@/lib/routes";
import { EJINSIGHT_COLORS, EJINSIGHT_TYPE } from "@/lib/theme/ejinsight";
import { SectionHeader } from "../shared/section-header";

export function ColumnistsSection() {
  return (
    <section
      id="columnists"
      className="w-full"
      style={{ backgroundColor: EJINSIGHT_COLORS.surfaceAlt }}
    >
      <div className="mx-auto w-full max-w-[1280px] px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
        <div className="mb-12">
          <SectionHeader
            eyebrow="OPINION"
            title="Voices from the Columnists"
            description="Independent commentary and analysis from EJ Insight's roster of contributors."
          />
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {columnistImages.map((c) => {
            const bio = columnistBios[c.name];
            return (
              /* TWO links, not one card-sized link to the columnist.

                 The whole card opened the writer's page, so the headline under
                 "Latest Column" — the one thing on the card that names an
                 article — opened a listing instead. QA: "OPINION: clicking on
                 the article should redirect to article details page". The
                 writer's row still opens their page; the column block opens the
                 column. Nesting one inside the other is not an option: an
                 anchor inside an anchor makes the browser close the outer one
                 early. */
              <div
                key={c.name}
                className="group hkej-tap flex flex-col gap-5 p-6 transition-all duration-300"
                style={{
                  backgroundColor: EJINSIGHT_COLORS.surface,
                  border: `1px solid ${EJINSIGHT_COLORS.hairline}`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = EJINSIGHT_COLORS.primary;
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = EJINSIGHT_COLORS.hairline;
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <Link
                  href={ejInsightColumnistRoute(c.name)}
                  className="flex items-center gap-4"
                >
                  <span
                    className="inline-block h-16 w-16 shrink-0 overflow-hidden rounded-full"
                    style={{ backgroundColor: EJINSIGHT_COLORS.primarySoft }}
                  >
                    <Image
                      src={c.img}
                      alt={c.name}
                      width={128}
                      height={128}
                      className="h-full w-full object-cover"
                      unoptimized
                    />
                  </span>
                  <div className="flex flex-col gap-1">
                    <span
                      style={{
                        ...EJINSIGHT_TYPE.eyebrow,
                        fontSize: 10,
                        color: EJINSIGHT_COLORS.primary,
                      }}
                    >
                      {bio?.role ?? "Contributor"}
                    </span>
                    <span
                      style={{
                        fontFamily: EJINSIGHT_TYPE.cardTitle.fontFamily,
                        fontSize: 18,
                        fontWeight: 600,
                        color: EJINSIGHT_COLORS.ink,
                      }}
                    >
                      {c.name}
                    </span>
                  </div>
                </Link>

                <Link
                  href={EJINSIGHT_ARTICLE_ROUTE}
                  className="flex flex-col gap-2 pt-5"
                  style={{ borderTop: `1px solid ${EJINSIGHT_COLORS.hairlineSoft}` }}
                >
                  <span
                    style={{
                      ...EJINSIGHT_TYPE.eyebrow,
                      fontSize: 10,
                      color: EJINSIGHT_COLORS.muted,
                    }}
                  >
                    Latest Column
                  </span>
                  <h3
                    className="hkej-tap-title m-0 line-clamp-2"
                    style={{
                      ...EJINSIGHT_TYPE.cardTitle,
                      fontSize: 16,
                      color: EJINSIGHT_COLORS.ink,
                    }}
                  >
                    {bio?.latest ?? "—"}
                  </h3>
                  <div className="mt-2 flex items-center justify-between">
                    <span
                      style={{
                        ...EJINSIGHT_TYPE.meta,
                        color: EJINSIGHT_COLORS.muted,
                      }}
                    >
                      {bio?.date ?? ""}
                    </span>
                    <span
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      style={{
                        backgroundColor: EJINSIGHT_COLORS.primarySoft,
                        color: EJINSIGHT_COLORS.primary,
                      }}
                    >
                      <ArrowUpRight className="h-4 w-4" strokeWidth={2.4} />
                    </span>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
